'use client';

import { FC, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ProjectCollaborator from './ProjectCollaborator';
import { CollaboratorDto, ProjectCollaboratorDto, PrivilegeEnum, RoleDto } from '@/api/ApiClient';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { filterOtherProjectRoles, isOtherCollaboratorRole } from '@/lib/utils/roleUtils';
import UserCollaboratorDialog, {
  type UserCollaboratorCreatePreset,
} from '@/components/customizing/UserCollaboratorDialog';
import { getProjectMemberUserIds, ProjectUIModel } from '@/lib/models/projectUIModel';
import { formatCollaboratorOptionLabel, parseCollaboratorCreateInput } from '@/lib/options/userUtils';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { dedupeCollaboratorsByUserId } from '@/lib/users/selectableUsers';

interface CollaboratorsTabProps {
  project: ProjectUIModel;
  isReadOnly: boolean;
  onUpdateCollaborators: (collaborators: ProjectCollaboratorDto[]) => void;
}

const selectOptions: OptionKey[] = ['users', 'roles'];

type AddRowUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  institution?: string;
  contactEmail?: string;
  email?: string;
  /** Raw typed text when this row is the on-the-fly create action. */
  createInput?: string;
};

const CREATE_USER_OPTION_ID = '__create_user__';

function additionalCollaboratorCreatePreset(prefill?: {
  firstName?: string;
  lastName?: string;
  email?: string;
}): UserCollaboratorCreatePreset {
  return {
    userKind: 'external',
    lockUserKind: true,
    rolePredicate: (role) => !role.isInhouse,
    helperText: 'Additional collaborators are external users.',
    initialFirstName: prefill?.firstName,
    initialLastName: prefill?.lastName,
    initialEmail: prefill?.email,
  };
}

const CollaboratorsTab: FC<CollaboratorsTabProps> = ({ project, isReadOnly, onUpdateCollaborators }) => {
  const { isAuthorized } = usePrivileges();
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const [showAddRow, setShowAddRow] = useState(false);
  const [isUserCreateOpen, setIsUserCreateOpen] = useState(false);
  const [userCreatePrefill, setUserCreatePrefill] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null>(null);
  const [addRowPrefillUserId, setAddRowPrefillUserId] = useState<string | null>(null);
  const [createdUserForAddRow, setCreatedUserForAddRow] = useState<AddRowUser | null>(null);

  const otherCollaborators = project.otherCollaborators ?? [];
  const canCreateUser = isAuthorized(PrivilegeEnum.User, 'create');
  const projectMemberIds = useMemo(() => getProjectMemberUserIds(project), [project]);

  const availableUsers = useMemo(
    () => dedupeCollaboratorsByUserId(options.users).filter((user) => user.id && !projectMemberIds.has(user.id)),
    [options.users, projectMemberIds]
  );
  const availableRoles = filterOtherProjectRoles(options.roles);

  const usersForAddRow = useMemo((): AddRowUser[] => {
    const extras: AddRowUser[] = [];
    if (addRowPrefillUserId && !availableUsers.some((u) => u.id === addRowPrefillUserId)) {
      const fromOptions = options.users?.find((u) => u.id === addRowPrefillUserId);
      if (fromOptions?.id) {
        extras.push(fromOptions);
      } else if (createdUserForAddRow?.id === addRowPrefillUserId) {
        extras.push(createdUserForAddRow);
      }
    }
    return extras.length ? [...availableUsers, ...extras] : availableUsers;
  }, [addRowPrefillUserId, availableUsers, createdUserForAddRow, options.users]);

  const addCollaboratorDisabled = availableUsers.length === 0 && !addRowPrefillUserId && !canCreateUser;
  const addCollaboratorDisabledTitle = optionsLoading
    ? 'Loading users...'
    : !options.users?.length && !canCreateUser
      ? 'User list is not available'
      : addCollaboratorDisabled
        ? 'No users available to add'
        : undefined;

  const handleAddCollaborator = (userId: string, roleId: number) => {
    if (!isOtherCollaboratorRole(roleId)) return;

    const selectedUser = usersForAddRow.find((u) => u.id === userId) ?? options.users?.find((u) => u.id === userId);
    const selectedRole = availableRoles.find((r) => r.id === roleId);

    if (!selectedUser || !selectedRole) return;

    const newCollaborator: ProjectCollaboratorDto = {
      id: selectedUser.id,
      firstName: selectedUser.firstName || '',
      lastName: selectedUser.lastName || '',
      institution: selectedUser.institution || '',
      roleId: roleId,
      role: selectedRole.name,
    };

    onUpdateCollaborators([...otherCollaborators, newCollaborator]);
    setShowAddRow(false);
    setAddRowPrefillUserId(null);
    setCreatedUserForAddRow(null);
  };

  const handleCancelAdd = () => {
    setShowAddRow(false);
    setAddRowPrefillUserId(null);
    setCreatedUserForAddRow(null);
  };

  const handleUpdateCollaborator = (index: number, updatedCollaborator: ProjectCollaboratorDto) => {
    if (!isOtherCollaboratorRole(updatedCollaborator.roleId)) return;

    const updatedCollaborators = [...otherCollaborators];
    updatedCollaborators[index] = updatedCollaborator;
    onUpdateCollaborators(updatedCollaborators);
  };

  const handleRemoveCollaborator = (index: number) => {
    onUpdateCollaborators(otherCollaborators.filter((_, i) => i !== index));
  };

  const openUserCreate = (typedInput?: string) => {
    setUserCreatePrefill(typedInput ? parseCollaboratorCreateInput(typedInput) : null);
    setIsUserCreateOpen(true);
  };

  const handleUserCreated = async (saved: CollaboratorDto) => {
    if (!saved.id) return;
    setCreatedUserForAddRow({
      id: saved.id,
      firstName: saved.firstName,
      lastName: saved.lastName,
      institution: saved.institution,
      contactEmail: saved.contactEmail,
      email: saved.email,
    });
    setAddRowPrefillUserId(saved.id);
    setShowAddRow(true);
  };

  const findCollaboratorIndex = (collaborator: ProjectCollaboratorDto): number =>
    otherCollaborators.findIndex((c) => c.id === collaborator.id && c.roleId === collaborator.roleId);

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        {!isReadOnly ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
              Beyond PI, faculty, and staff (see Details tab).
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {canCreateUser ? (
                <Button variant="outlined" size="small" onClick={() => openUserCreate()}>
                  Add new User
                </Button>
              ) : null}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddRow(true)}
                size="small"
                disabled={addCollaboratorDisabled}
                title={addCollaboratorDisabledTitle}
              >
                Add collaborator
              </Button>
            </Box>
          </Box>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Institution</TableCell>
                {!isReadOnly ? <TableCell width={60}>Actions</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {otherCollaborators.length === 0 && !showAddRow ? (
                <TableRow>
                  <TableCell colSpan={isReadOnly ? 3 : 4} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    No additional collaborators
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {otherCollaborators.map((collaborator, index) => (
                    <ProjectCollaborator
                      key={`${collaborator.id}-${collaborator.roleId}-${index}`}
                      collaborator={collaborator}
                      isReadOnly={isReadOnly}
                      availableRoles={availableRoles}
                      rolesLoading={optionsLoading}
                      onUpdate={(updatedCollaborator) => {
                        const fullIndex = findCollaboratorIndex(collaborator);
                        if (fullIndex !== -1) {
                          handleUpdateCollaborator(fullIndex, updatedCollaborator);
                        }
                      }}
                      onRemove={() => {
                        const fullIndex = findCollaboratorIndex(collaborator);
                        if (fullIndex !== -1) {
                          handleRemoveCollaborator(fullIndex);
                        }
                      }}
                    />
                  ))}
                  {showAddRow && !isReadOnly ? (
                    <AddCollaboratorRow
                      key={addRowPrefillUserId ?? 'add'}
                      availableUsers={usersForAddRow}
                      availableRoles={availableRoles}
                      initialUserId={addRowPrefillUserId}
                      canCreateUser={canCreateUser}
                      onAdd={handleAddCollaborator}
                      onCancel={handleCancelAdd}
                      onCreateUser={openUserCreate}
                    />
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      {isUserCreateOpen ? (
        <UserCollaboratorDialog
          open={isUserCreateOpen}
          record={null}
          isEdit={false}
          createPreset={additionalCollaboratorCreatePreset(userCreatePrefill ?? undefined)}
          onSave={handleUserCreated}
          onClose={() => {
            setIsUserCreateOpen(false);
            setUserCreatePrefill(null);
          }}
        />
      ) : null}
    </Paper>
  );
};

interface AddCollaboratorRowProps {
  availableUsers: AddRowUser[];
  availableRoles: RoleDto[];
  initialUserId?: string | null;
  canCreateUser: boolean;
  onAdd: (userId: string, roleId: number) => void;
  onCancel: () => void;
  onCreateUser: (typedInput: string) => void;
}

const AddCollaboratorRow: FC<AddCollaboratorRowProps> = ({
  availableUsers,
  availableRoles,
  initialUserId,
  canCreateUser,
  onAdd,
  onCancel,
  onCreateUser,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(initialUserId ?? undefined);
  const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>(undefined);

  const selectedUser = availableUsers.find((u) => u.id === selectedUserId);
  const selectedRole = availableRoles.find((r) => r.id === selectedRoleId);

  const handleAdd = () => {
    if (selectedUserId && selectedRoleId) {
      onAdd(selectedUserId, selectedRoleId);
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Autocomplete
          value={selectedUser || null}
          onChange={(_, newValue) => {
            if (newValue?.id === CREATE_USER_OPTION_ID) {
              onCreateUser(newValue.createInput ?? '');
              return;
            }
            setSelectedUserId(newValue?.id ?? undefined);
          }}
          options={availableUsers}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) =>
            option.id === CREATE_USER_OPTION_ID
              ? `Add new user "${option.createInput ?? ''}"`
              : formatCollaboratorOptionLabel(option)
          }
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          filterOptions={(opts, params) => {
            const filtered = filterAutocompleteOptionsByMatchRank(opts, params, formatCollaboratorOptionLabel);
            const typed = params.inputValue.trim();
            if (canCreateUser && typed) {
              filtered.push({ id: CREATE_USER_OPTION_ID, createInput: typed });
            }
            return filtered;
          }}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Select user..." fullWidth />}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          value={selectedRole || null}
          onChange={(_, newValue) => setSelectedRoleId(newValue?.id ?? undefined)}
          options={availableRoles}
          getOptionKey={(option) => String(option.id)}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Select role..." fullWidth />}
        />
      </TableCell>
      <TableCell>
        <TextField
          value={selectedUser?.institution || ''}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
          variant="outlined"
          placeholder="Auto-populated"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={handleAdd} disabled={!selectedUserId || !selectedRoleId}>
            Add
          </Button>
          <Button size="small" variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default CollaboratorsTab;
